import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Dog, MapPin, Phone, Mail, Globe, Plus, Check, 
  Loader2, ArrowRight, ChevronDown, Camera, 
  Facebook, Instagram, Shield, Clock, Star, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import { useTranslation } from 'react-i18next';
import TopNavBar from '@/components/TopNavBar';
import MarketplaceFooter from '@/components/MarketplaceFooter';
import { useAuth } from '@/context/AuthContext';

const VOIVODESHIPS = [
  "dolnośląskie", "kujawsko-pomorskie", "lubelskie", "lubuskie",
  "łódzkie", "małopolskie", "mazowieckie", "opolskie",
  "podkarpackie", "podlaskie", "pomorskie", "śląskie",
  "świętokrzyskie", "warmińsko-mazurskie", "wielkopolskie", "zachodniopomorskie"
];

const POPULAR_BREEDS = [
  "Golden Retriever", "Owczarek Niemiecki", "Labrador Retriever", "Bulldog Francuski",
  "Yorkshire Terrier", "Maltańczyk", "Shih Tzu", "Chihuahua", "Border Collie",
  "Cavalier King Charles Spaniel", "Beagle", "Boxer", "Pomeranian", "Jack Russell Terrier",
  "Husky Syberyjski", "Doberman", "Rottweiler", "Cocker Spaniel Angielski",
  "Welsh Corgi Pembroke", "Cane Corso", "Bernese Mountain Dog", "Owczarek Podhalański",
  "Australian Shepherd", "Akita Inu", "Shiba Inu", "Dalmatyńczyk", "Pudel",
  "Bichon Frise", "West Highland White Terrier", "Miniature Schnauzer"
];

// ==================== SUCCESS SCREEN ====================
function SuccessScreen({ editUrl, flow, isAuth }) {
  const flowCopy = flow === 'miot'
    ? { title: 'Pierwszy krok gotowy!', subtitle: isAuth
        ? 'Sprawdzimy dane i aktywujemy hodowlę (do 24h). Gdy to się stanie, dostaniesz maila i od razu dodasz szczenięta.'
        : 'Za chwilę dostaniesz e-mail. Kliknij link w wiadomości, żeby od razu dodać szczenięta do swojej hodowli.',
        next: 'szczeniąt' }
    : flow === 'reproduktor'
      ? { title: 'Pierwszy krok gotowy!', subtitle: isAuth
          ? 'Sprawdzimy dane i aktywujemy hodowlę (do 24h). Gdy to się stanie, dostaniesz maila i od razu dodasz reproduktora.'
          : 'Za chwilę dostaniesz e-mail. Kliknij link w wiadomości, żeby od razu dodać reproduktora do swojej hodowli.',
          next: 'reproduktora' }
      : null;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-lg mx-auto text-center py-12"
      data-testid="add-breeder-success"
    >
      <div className="w-20 h-20 bg-emerald-500/20 border-2 border-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <Check className="w-10 h-10 text-emerald-400" />
      </div>

      <h2 className="text-3xl font-black text-white mb-3" data-testid="success-title">
        {flowCopy?.title || 'Hodowla została zgłoszona!'}
      </h2>
      <p className="text-gray-400 mb-6 text-lg">
        {flowCopy?.subtitle || (isAuth
          ? 'Sprawdzimy dane i aktywujemy hodowlę w ciągu 24h.'
          : 'Sprawdzimy dane i dodamy Twoją hodowlę do katalogu. To zajmie do 24h.')}
      </p>

      {flowCopy && !isAuth && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-5 mb-6 text-left">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 font-semibold text-sm">Sprawdź skrzynkę (i spam)</span>
          </div>
          <p className="text-white/70 text-sm">
            Po kliknięciu linku aktywacyjnego wrócisz tu i od razu dokończysz dodawanie {flowCopy.next}. Bez dublowania danych.
          </p>
        </div>
      )}

      {/* EDIT LINK — tylko dla gości (nie-zalogowany) */}
      {editUrl && !isAuth && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-8 text-left">
          <p className="text-amber-400 font-semibold text-sm mb-2">Zapisz ten link — do edycji i usuwania wpisu:</p>
          <div className="flex items-center gap-2">
            <input
              type="text" readOnly value={`${window.location.origin}${editUrl}`}
              className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-amber-500/20 text-white text-xs font-mono"
              onClick={e => { e.target.select(); navigator.clipboard.writeText(e.target.value); }}
              data-testid="edit-link-input"
            />
            <button
              onClick={() => { navigator.clipboard.writeText(`${window.location.origin}${editUrl}`); }}
              className="px-3 py-2 rounded-lg bg-amber-500 text-black text-xs font-bold hover:bg-amber-400"
            >
              Kopiuj
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-2">Kliknij w link aby edytować lub usunąć hodowlę.</p>
        </div>
      )}

      {/* Zalogowany użytkownik — CTA do panelu (zamiast rejestracji) */}
      {isAuth ? (
        <Link to="/panel">
          <Button
            className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-bold px-8 py-5 rounded-full text-base w-full"
            data-testid="cta-panel"
          >
            Przejdź do panelu
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </Link>
      ) : (
        /* Gość — CTA do rejestracji */
        <div className="bg-gradient-to-br from-blue-600/20 via-orange-600/20 to-orange-500/20 border border-white/10 rounded-2xl p-8 mb-8">
          <h3 className="text-xl font-bold text-white mb-2">
            Chcesz zarządzać swoją hodowlą?
          </h3>
          <p className="text-gray-400 mb-6">
            Zarejestruj się w ApisAI i zyskaj pełen zestaw narzędzi:
          </p>

          <div className="space-y-3 text-left max-w-sm mx-auto mb-8">
            {[
              { icon: Dog, text: "Dodawanie miotów i szczeniąt z galerią" },
              { icon: Zap, text: "Automatyczna promocja w social media" },
              { icon: Star, text: "Profesjonalna strona miotu + landing page" },
            ].map(({ icon: Icon, text }, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-gray-300 text-sm">{text}</span>
              </div>
            ))}
          </div>

          <Link to="/rejestracja">
            <Button
              className="bg-gradient-to-r from-blue-500 to-orange-500 hover:from-blue-600 hover:to-orange-600 text-white font-bold px-8 py-5 rounded-full text-base w-full"
              data-testid="cta-register"
            >
              Zarejestruj się za darmo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      )}

      <Link to="/rasy" className="text-gray-500 hover:text-white transition-colors text-sm block mt-6">
        Wróć do katalogu ras
      </Link>
    </motion.div>
  );
}

// ==================== BREED SELECTOR ====================
function BreedSelector({ selected, onChange }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = POPULAR_BREEDS.filter(b =>
    b.toLowerCase().includes(search.toLowerCase()) && !selected.includes(b)
  );

  const addBreed = (breed) => {
    onChange([...selected, breed]);
    setSearch('');
    setIsOpen(false);
  };

  const removeBreed = (breed) => {
    onChange(selected.filter(b => b !== breed));
  };

  const addCustom = () => {
    if (search.trim() && !selected.includes(search.trim())) {
      onChange([...selected, search.trim()]);
      setSearch('');
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Rasa / rasy <span className="text-red-400">*</span>
      </label>
      
      {/* Selected breeds */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {selected.map(breed => (
            <span key={breed} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full text-sm text-blue-300">
              {breed}
              <button onClick={() => removeBreed(breed)} className="hover:text-white">&times;</button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder={selected.length > 0 ? "Dodaj kolejną rasę..." : "Wpisz nazwę rasy..."}
          className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
          data-testid="breed-input"
        />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (search || filtered.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="absolute z-50 w-full mt-1 bg-[#10182B] border border-white/10 rounded-xl max-h-48 overflow-y-auto shadow-2xl"
          >
            {filtered.slice(0, 8).map(breed => (
              <button
                key={breed}
                onClick={() => addBreed(breed)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
              >
                {breed}
              </button>
            ))}
            {search.trim() && !POPULAR_BREEDS.some(b => b.toLowerCase() === search.toLowerCase()) && (
              <button
                onClick={addCustom}
                className="w-full text-left px-4 py-2.5 text-sm text-blue-400 hover:bg-blue-500/10 transition-colors border-t border-white/5"
              >
                <Plus className="w-3.5 h-3.5 inline mr-1.5" />
                Dodaj "{search.trim()}"
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ==================== MAIN COMPONENT ====================
export default function AddBreederPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preselectedBreed = searchParams.get('rasa');
  const flow = searchParams.get('flow'); // 'miot' | 'reproduktor' | null
  const isFlowMode = flow === 'miot' || flow === 'reproduktor';
  
  const [submitted, setSubmitted] = useState(false);
  const [editUrl, setEditUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    kennel_name: '',
    breeds: preselectedBreed ? [preselectedBreed] : [],
    city: '',
    voivodeship: '',
    phone: '',
    email: '',
    description: '',
    website: '',
    facebook: '',
    instagram: '',
    organization_type: 'unknown',
    is_fci: false,
  });
  const [showOptional, setShowOptional] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [resendLoading, setResendLoading] = useState(false);

  // Auto-fill email for logged-in user (np. po soft-signup)
  useEffect(() => {
    if (user?.email && !form.email) {
      setForm(prev => ({ ...prev, email: user.email }));
    }
  }, [user, form.email]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.kennel_name.trim()) return toast.error('Podaj nazwę hodowli');
    if (form.breeds.length === 0) return toast.error('Wybierz przynajmniej jedną rasę');
    if (!form.city.trim()) return toast.error('Podaj miasto');
    if (!form.phone.trim() && !form.email.trim()) return toast.error('Podaj telefon lub email');

    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/breeder-submit`, { ...form, flow: flow || undefined });
      if (res.data.edit_url) setEditUrl(res.data.edit_url);
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      // Duplikat hodowli — zaproponuj wysyłkę linku do edycji na email
      if (status === 409 && typeof detail === 'string') {
        setDuplicateError(detail);
        window.scrollTo(0, 0);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Wystąpił błąd. Spróbuj ponownie.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async () => {
    if (!form.email.trim()) {
      toast.error('Podaj email — wyślemy na niego link do edycji');
      return;
    }
    setResendLoading(true);
    try {
      await axios.post(`${API}/public/breeder-resend-link`, {
        kennel_name: form.kennel_name,
        email: form.email,
      });
      toast.success('Jeśli podany email pasuje, wysłaliśmy link na skrzynkę. Sprawdź też SPAM.');
      setDuplicateError(null);
    } catch (_) {
      toast.error('Coś poszło nie tak. Napisz na kontakt@apisai.pl');
    } finally {
      setResendLoading(false);
    }
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Hodowla zgłoszona! | ApisAI</title>
        </Helmet>
        <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <TopNavBar />
          <div className="px-6 py-20">
            <SuccessScreen editUrl={editUrl} flow={flow} isAuth={!!user} />
          </div>
          <MarketplaceFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dodaj hodowlę za darmo | ApisAI</title>
        <meta name="description" content="Dodaj swoją hodowlę do katalogu ApisAI. Pokaż szczenięta tysiącom potencjalnych klientów. Rejestracja nie jest wymagana." />
      </Helmet>

      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <TopNavBar />

        <div className="pt-10 pb-20 px-6">
          <div className="max-w-xl mx-auto">
            {duplicateError && (
              <div className="mb-6 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-5" data-testid="duplicate-banner">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">ℹ️</div>
                  <div className="flex-1">
                    <h3 className="text-amber-300 font-bold mb-2">Ta hodowla już jest u nas</h3>
                    <p className="text-sm text-amber-100/90 mb-4 leading-relaxed">{duplicateError}</p>
                    <div className="bg-black/20 rounded-xl p-3 border border-amber-500/20">
                      <p className="text-xs text-amber-200/80 mb-2">
                        Podaj swój email — wyślemy link do edycji hodowli (musi pasować do tego, który mamy w bazie):
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="email"
                          value={form.email}
                          onChange={(e) => updateField('email', e.target.value)}
                          placeholder="twoj-email@gmail.com"
                          className="flex-1 px-3 py-2 rounded-lg bg-black/40 border border-amber-500/30 text-white placeholder-white/30 text-sm focus:border-amber-400 focus:outline-none"
                          data-testid="duplicate-email-input"
                        />
                        <button
                          type="button"
                          onClick={handleResendLink}
                          disabled={resendLoading || !form.email.trim()}
                          className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="duplicate-resend-btn"
                        >
                          {resendLoading ? 'Wysyłanie…' : 'Wyślij link'}
                        </button>
                      </div>
                      <p className="text-[11px] text-amber-200/60 mt-2">
                        Nie pamiętasz emaila lub nie pasuje? Napisz na <a href="mailto:kontakt@apisai.pl" className="underline">kontakt@apisai.pl</a>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDuplicateError(null)}
                      className="mt-3 text-xs text-amber-300/70 hover:text-amber-300 underline"
                    >
                      Zamknij i spróbuj z inną nazwą
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="text-center mb-8">
              {isFlowMode ? (
                <div className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-sm mb-6 w-full sm:w-auto" data-testid="flow-stepper">
                  <span className="flex items-center gap-1.5 text-emerald-300 font-semibold">
                    <span className="w-6 h-6 rounded-full bg-emerald-500 text-black flex items-center justify-center font-bold text-xs">1</span>
                    Hodowla
                  </span>
                  <span className="text-white/30">→</span>
                  <span className="flex items-center gap-1.5 text-white/50">
                    <span className="w-6 h-6 rounded-full border border-white/20 text-white/60 flex items-center justify-center font-bold text-xs">2</span>
                    {flow === 'miot' ? 'Szczenięta' : 'Reproduktor'}
                  </span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
                  <Plus className="w-4 h-4" />
                  Darmowe zgłoszenie
                </div>
              )}
              <h1 className="text-3xl sm:text-4xl font-black text-white mb-3" data-testid="add-breeder-title">
                {isFlowMode
                  ? (flow === 'miot' ? 'Najpierw hodowla — potem szczenięta' : 'Najpierw hodowla — potem reproduktor')
                  : 'Dodaj hodowlę'}
              </h1>
              <p className="text-gray-400">
                {isFlowMode
                  ? 'To zajmie minutę. Po kliknięciu linku z e-maila wrócisz tu i od razu dodasz '
                    + (flow === 'miot' ? 'szczenięta.' : 'reproduktora.')
                  : 'Pojawi się w katalogu ras po weryfikacji. Bez rejestracji.'}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Kennel name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Nazwa hodowli <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.kennel_name}
                  onChange={e => updateField('kennel_name', e.target.value)}
                  placeholder="np. Golden Valley FCI"
                  className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                  data-testid="kennel-name-input"
                />
              </div>

              {/* Breeds */}
              <BreedSelector
                selected={form.breeds}
                onChange={breeds => updateField('breeds', breeds)}
              />

              {/* City + Voivodeship */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Miasto <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.city}
                    onChange={e => updateField('city', e.target.value)}
                    placeholder="np. Kraków"
                    className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Województwo
                  </label>
                  <select
                    value={form.voivodeship}
                    onChange={e => updateField('voivodeship', e.target.value)}
                    className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-blue-500/50 focus:outline-none appearance-none"
                    data-testid="voivodeship-select"
                  >
                    <option value="">Wybierz...</option>
                    {VOIVODESHIPS.map(v => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Telefon <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={e => updateField('phone', e.target.value)}
                      placeholder="600 100 200"
                      className="w-full bg-[#10182B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                      data-testid="phone-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={e => updateField('email', e.target.value)}
                      placeholder="kontakt@hodowla.pl"
                      className="w-full bg-[#10182B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                      data-testid="email-input"
                    />
                  </div>
                </div>
              </div>

              <p className="text-gray-600 text-xs">* Telefon lub email jest wymagany</p>

              {/* Organization - minimal */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Organizacja
                  </label>
                  <select
                    value={form.organization_type}
                    onChange={e => {
                      const val = e.target.value;
                      updateField('organization_type', val);
                      updateField('is_fci', val === 'zkwp_fci' || val === 'fci_other');
                    }}
                    className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-blue-500/50 focus:outline-none appearance-none"
                    data-testid="organization-type-select"
                  >
                    <option value="unknown">Brak / nie podano</option>
                    <option value="zkwp_fci">ZKwP (FCI)</option>
                    <option value="fci_other">Inna FCI</option>
                    <option value="khron">KHRON</option>
                    <option value="wush">WUSH</option>
                    <option value="other">Inna organizacja</option>
                  </select>
                </div>
                <div className="flex items-end pb-1">
                  {form.is_fci && (
                    <div className="flex items-center gap-2 text-sm text-blue-400 bg-blue-500/10 border border-blue-500/20 px-3 py-2.5 rounded-xl w-full">
                      <Shield className="w-4 h-4" />
                      <span className="font-medium">FCI</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Optional fields toggle */}
              <button
                type="button"
                onClick={() => setShowOptional(!showOptional)}
                className="w-full text-left flex items-center justify-between px-4 py-3 bg-[#10182B]/50 border border-white/5 rounded-xl text-sm text-gray-400 hover:text-white transition-colors"
              >
                <span>Pola dodatkowe (opis, www, social media)</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showOptional ? 'rotate-180' : ''}`} />
              </button>

              <AnimatePresence>
                {showOptional && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="space-y-4 overflow-hidden"
                  >
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Opis hodowli</label>
                      <textarea
                        value={form.description}
                        onChange={e => updateField('description', e.target.value)}
                        placeholder="Krótki opis Twojej hodowli..."
                        rows={3}
                        className="w-full bg-[#10182B] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none resize-none"
                        data-testid="description-input"
                      />
                    </div>

                    {/* Website */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Strona www</label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                        <input
                          type="url"
                          value={form.website}
                          onChange={e => updateField('website', e.target.value)}
                          placeholder="https://www.hodowla.pl"
                          className="w-full bg-[#10182B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Social */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Facebook</label>
                        <div className="relative">
                          <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <input
                            type="text"
                            value={form.facebook}
                            onChange={e => updateField('facebook', e.target.value)}
                            placeholder="facebook.com/hodowla"
                            className="w-full bg-[#10182B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Instagram</label>
                        <div className="relative">
                          <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                          <input
                            type="text"
                            value={form.instagram}
                            onChange={e => updateField('instagram', e.target.value)}
                            placeholder="@hodowla"
                            className="w-full bg-[#10182B] border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-5 rounded-xl text-base disabled:opacity-50"
                data-testid="submit-breeder-btn"
              >
                {loading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Wysyłanie...</>
                ) : (
                  <><Plus className="w-5 h-5 mr-2" /> Dodaj hodowlę</>
                )}
              </Button>

              <p className="text-center text-gray-600 text-xs flex items-center justify-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                Dodanie hodowli zajmuje mniej niż 30 sekund
              </p>

              {/* Trust signals */}
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-white/5">
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Shield className="w-3.5 h-3.5" /> Weryfikacja
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Check className="w-3.5 h-3.5" /> Darmowe
                </span>
                <span className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Star className="w-3.5 h-3.5" /> Bez rejestracji
                </span>
              </div>
            </form>
          </div>
        </div>

        <MarketplaceFooter />
      </div>
    </>
  );
}
